/**
 * Utility functions for Azure Referral Form
 */
import type {
  AzureMpcReferralType,
  DealType,
  ReferralStatus,
  ReferralSubstatus,
} from './azureReferralSchema'
import {
  AZURE_DEAL_TYPE,
  AZURE_FORM_STATUS,
  AZURE_MPC_REFERRAL_TYPE,
  AZURE_REFERRAL_STATUS,
  AZURE_REFERRAL_SUBSTATUS,
  SHARED_AZURE_DEAL_TYPES,
  type AzureFormStatus,
  isAzureDealType,
  isTerminalReferralSubstatus,
  matchesReferralStatus,
  matchesReferralSubstatus,
} from './azureReferralSchema'
import { getHubspotCosellAccountIdFromPath } from '../../../../common/utils/Hooks/partnerStateSelectors'
import { CustomerDetails, DealDetails, HelpFromMicrosoft } from './types'

type AzureReferralMetadataSlice = {
  partnerId?: string
  partner_id?: string
  referralId?: string
  crmPrimaryKeyValue?: string
}

type ResolveAzurePartnerIdParams = {
  pathname: string
  accountIdFromPath?: string
  tenantPartnerId?: string
  azureReferralMetadata?: AzureReferralMetadataSlice
}

export const resolveAzurePartnerId = ({
  pathname,
  accountIdFromPath,
  tenantPartnerId,
  azureReferralMetadata,
}: ResolveAzurePartnerIdParams): string =>
  getHubspotCosellAccountIdFromPath(pathname) ||
  accountIdFromPath ||
  azureReferralMetadata?.partnerId ||
  azureReferralMetadata?.partner_id ||
  tenantPartnerId ||
  ''

export const isAzureReferralRouteReady = (
  referralId: string | undefined,
  filledForm: AzureReferralFormHydrationSlice,
  azureReferralMetadata?: AzureReferralMetadataSlice
): boolean =>
  Boolean(referralId) &&
  (hasAzureReferralFormMappedData(filledForm) ||
    Boolean(
      azureReferralMetadata?.referralId ||
        azureReferralMetadata?.crmPrimaryKeyValue
    ))

type AzureReferralFormHydrationSlice = {
  customerDetails?: CustomerDetails
  dealDetails?: DealDetails
}

export { AZURE_MPC_REFERRAL_TYPE }
export type { AzureMpcReferralType, DealType }

export const NO_HELP_REQUIRED = 'No help required at this time'

export const hasAzureReferralFormMappedData = (
  filledForm: AzureReferralFormHydrationSlice
): boolean =>
  Boolean(
    filledForm.dealDetails?.dealName ||
      filledForm.customerDetails?.customerName ||
      filledForm.dealDetails?.crmId
  )

export const getAzureReferralFormikKey = (
  referralId: string | undefined,
  filledForm: AzureReferralFormHydrationSlice
): string => {
  if (!referralId) {
    return 'new-azure-referral'
  }

  return hasAzureReferralFormMappedData(filledForm)
    ? `${referralId}-hydrated`
    : `${referralId}-pending`
}

export const isAzureReferralFormLoadPending = (
  referralId: string | undefined,
  filledForm: AzureReferralFormHydrationSlice
): boolean => Boolean(referralId) && !hasAzureReferralFormMappedData(filledForm)

export const isMicrosoftHelpRequested = (typeOfHelp?: string) =>
  Boolean(typeOfHelp) && typeOfHelp !== NO_HELP_REQUIRED

/**
 * Deal types driven by seller visibility when no Microsoft help is requested.
 */
export const getDealTypeForSellerDealView = (
  sellerDealView: NonNullable<HelpFromMicrosoft['sellerDealView']>
): DealType =>
  sellerDealView === 'YES'
    ? AZURE_DEAL_TYPE.PARTNER_LED
    : AZURE_DEAL_TYPE.PRIVATE

export const mapFormDealTypeToReferralType = (
  dealType?: DealType | string
): AzureMpcReferralType =>
  dealType && (SHARED_AZURE_DEAL_TYPES as readonly string[]).includes(dealType)
    ? AZURE_MPC_REFERRAL_TYPE.SHARED
    : AZURE_MPC_REFERRAL_TYPE.INDEPENDENT

export { isAzureDealType }

const emptyHelpContactFields: Pick<
  HelpFromMicrosoft,
  'firstName' | 'lastName' | 'phone' | 'email'
> = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
}

/**
 * Updates Help from Microsoft when type of help changes.
 * - Help requested: keep contacts; clear seller visibility (follow-up is hidden).
 * - No help: clear contacts; default seller visibility to YES (Partner-led).
 */
export const getHelpFromMicrosoftForTypeOfHelp = ({
  helpFromMicrosoft,
  typeOfHelp,
}: {
  helpFromMicrosoft: HelpFromMicrosoft
  typeOfHelp: HelpFromMicrosoft['typeOfHelp']
}): HelpFromMicrosoft => {
  if (isMicrosoftHelpRequested(typeOfHelp)) {
    return {
      ...helpFromMicrosoft,
      typeOfHelp,
      sellerDealView: undefined,
    }
  }

  return {
    ...helpFromMicrosoft,
    typeOfHelp,
    ...emptyHelpContactFields,
    // PRD: default Yes → Partner-led; user can switch to No → Private
    sellerDealView: helpFromMicrosoft.sellerDealView ?? 'YES',
  }
}

export const isFormEditable = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  formStatus?: AzureFormStatus | string
): boolean => {
  if (formStatus === AZURE_FORM_STATUS.DRAFT) {
    return true
  }

  if (!status) {
    // If no status is set, allow editing (new referral create flow)
    return true
  }

  if (isTerminalReferralSubstatus(substatus)) {
    return false
  }

  if (matchesReferralStatus(status, AZURE_REFERRAL_STATUS.CLOSED)) {
    return false
  }

  if (
    matchesReferralStatus(status, AZURE_REFERRAL_STATUS.NEW) &&
    matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.PENDING)
  ) {
    return false
  }

  if (
    matchesReferralStatus(status, AZURE_REFERRAL_STATUS.NEW) &&
    matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.RECEIVED)
  ) {
    return true
  }

  if (
    matchesReferralStatus(status, AZURE_REFERRAL_STATUS.ACTIVE) &&
    matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.ACCEPTED)
  ) {
    return true
  }

  // All other combinations — read-only
  return false
}

/**
 * Determines if a specific field is editable based on status and field name
 */
export const isFieldEditable = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  fieldName?: string,
  formStatus?: AzureFormStatus | string
): boolean => {
  if (!isFormEditable(status, substatus, formStatus)) {
    return false
  }

  // If no field name provided, use general form editability
  if (!fieldName) {
    return true
  }

  // List of always-editable fields when form is editable
  const editableFields = [
    'dealName',
    'estimatedCloseDate',
    'estimatedValue',
    'partnerNotes',
    'notesToMicrosoft',
    'crmId',
    'marketingCampaignID',
    'teamMembers',
    'contactDetails',
    'solutionsOffered',
    'companySolutions',
    'microsoftSolutions',
    'selectedSolutions',
  ]

  return editableFields.includes(fieldName)
}

/**
 * Gets a user-friendly message explaining why the form is read-only*
 */
export const getReadOnlyMessage = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  formStatus?: AzureFormStatus | string
): string => {
  if (isFormEditable(status, substatus, formStatus)) {
    return ''
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.WON)) {
    return 'This referral has been won and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.LOST)) {
    return 'This referral has been lost and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.DECLINED)) {
    return 'This referral has been declined and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.ERROR)) {
    return 'This referral has an error and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.EXPIRED)) {
    return 'This referral has expired and cannot be edited. Referrals automatically expire after 14 days of inactivity.'
  }

  if (matchesReferralStatus(status, AZURE_REFERRAL_STATUS.CLOSED)) {
    return 'This referral is closed and cannot be edited.'
  }

  return 'This referral is not in an editable state.'
}
